import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { fechaLocal } from '../utils/fecha';
import { obtenerMovimientos, obtenerComprasPeriodo } from './balanceService';
import { obtenerCuentas } from './cuentasService';

// Corte de Caja Diario: resumen del día (cuánto entró/salió, ventas por canal)
// + sello del día cerrado con el conteo físico de efectivo. Reusa el libro
// unificado movimientos_cuenta y la tabla ventas; no crea dinero por su cuenta.

const INGRESO_TIPOS = ['venta', 'ingreso_libre', 'abono'];
const diaLocal = (iso) => fechaLocal(new Date(iso));
// Un día antes de `fecha` (para el filtro amplio que luego se recorta por día local).
const diaAntes = (fecha) => {
  const [y, m, d] = fecha.split('-').map(Number);
  return fechaLocal(new Date(y, m - 1, d - 1));
};

// Foto del día: ingresos, egresos, neto, ventas por canal y saldos de cuentas.
// Devuelve también las cuentas (para armar el conteo de efectivo en la vista).
export async function resumenDelDia(fecha) {
  const dia = fecha || fechaLocal();
  const [movsTodos, compras, cuentas] = await Promise.all([
    obtenerMovimientos({ desde: diaAntes(dia) }),
    obtenerComprasPeriodo({ desde: diaAntes(dia) }),
    obtenerCuentas(),
  ]);

  // Solo los movimientos de ESE día local (evita corrimientos por zona horaria).
  const movs = (movsTodos || []).filter((m) => diaLocal(m.creado_en) === dia);

  const ingresos = movs
    .filter((m) => INGRESO_TIPOS.includes(m.tipo))
    .reduce((s, m) => s + Number(m.monto || 0), 0);
  const egresos = movs
    .filter((m) => m.tipo === 'gasto')
    .reduce((s, m) => s + Math.abs(Number(m.monto || 0)), 0);

  // Movimiento del día por cuenta (lo que entró/salió de cada caja hoy).
  // Solo cuenta ingresos del negocio (venta/ingreso_libre/abono) y gastos, para
  // que el desglose SUME EXACTO con los totales «Entró/Salió» de arriba. Los
  // ajustes de cuadre, transferencias y saldos iniciales no entran aquí (son
  // internos; el efectivo se cuadra en su propia sección / en Cuentas).
  const porCuenta = {};
  cuentas.forEach((c) => { porCuenta[c.id] = { nombre: c.nombre, tipo: c.tipo, ingresos: 0, egresos: 0 }; });
  movs.forEach((m) => {
    if (!m.cuenta_id) return;
    const b = porCuenta[m.cuenta_id] || (porCuenta[m.cuenta_id] = { nombre: m.cuentas?.nombre || 'Cuenta', tipo: m.cuentas?.tipo || 'otro', ingresos: 0, egresos: 0 });
    if (INGRESO_TIPOS.includes(m.tipo)) b.ingresos += Number(m.monto || 0);
    else if (m.tipo === 'gasto') b.egresos += Math.abs(Number(m.monto || 0));
  });

  // Ventas del día por canal (INCLUYE fiado; una compra = un ticket).
  const comprasDia = (compras || []).filter((c) => diaLocal(c.fecha) === dia);
  const porCanalMap = {};
  comprasDia.forEach((c) => {
    const canal = c.canal || 'Sin canal';
    const b = porCanalMap[canal] || (porCanalMap[canal] = { canal, monto: 0, tickets: 0 });
    b.monto += Number(c.total || 0);
    b.tickets += 1;
  });
  const porCanal = Object.values(porCanalMap).sort((a, b) => b.monto - a.monto);
  const ventasDia = comprasDia.reduce((s, c) => s + Number(c.total || 0), 0);
  const ticketsDia = comprasDia.length;

  const saldoCuentas = cuentas.reduce((s, c) => s + Number(c.saldo || 0), 0);

  return {
    ingresos, egresos, neto: ingresos - egresos,
    ventasDia, ticketsDia, porCanal,
    porCuenta: Object.entries(porCuenta).map(([id, v]) => ({ cuenta_id: id, ...v })),
    saldoCuentas, cuentas,
    nMovimientos: movs.length,
  };
}

// --- Sello del día ---

export async function obtenerCortes() {
  const { data, error } = await supabase
    .from('cortes_dia')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('fecha', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function cerrarDia(fecha, resumen, conteos) {
  const { data, error } = await supabase.rpc('cerrar_dia', {
    p_negocio_id: getNegocioId(),
    p_fecha: fecha,
    p_resumen: resumen || {},
    p_conteos: conteos || [],
  });
  if (error) throw error;
  return data;
}

export async function reabrirDia(fecha) {
  const { error } = await supabase
    .from('cortes_dia')
    .delete()
    .eq('negocio_id', getNegocioId())
    .eq('fecha', fecha);
  if (error) throw error;
}
